import { wlFramePlace } from "@/lib/avatars/frames";
import type { AvatarCustomization } from "@/types/game";
import {
  EXTRA_SLOTS,
  getAvatarPart,
  DEFAULT_HAIR_ID,
  DEFAULT_JERSEY_ID,
  DEFAULT_SKIN_ID,
  FACIAL_HAIR_IDS,
  GLASSES_IDS,
  HAIR_IDS,
  JERSEY_IDS,
  SKIN_IDS,
  getSkinPart,
} from "@/lib/avatars/parts";

/** Available avatar color keys (kept for legacy callers — used only by the picker swatch hint). */
export const AVATAR_COLORS = ["green", "blue", "yellow", "red", "violet", "pink"] as const;
export type AvatarColor = (typeof AVATAR_COLORS)[number];

/** Hex swatches used in the picker (matches the body of the rendered jersey). */
export const AVATAR_COLOR_SWATCHES: Record<AvatarColor, string> = {
  green: "#21B41B",
  blue: "#1F2DAB",
  yellow: "#FFEC1F",
  red: "#FB3101",
  violet: "#BA02E8",
  pink: "#E933A6",
};

/** Default avatar id used by legacy code paths that read a single string. */
export const DEFAULT_AVATAR_PRIMARY = DEFAULT_SKIN_ID;
export const DEFAULT_AVATAR_SECONDARY = DEFAULT_SKIN_ID;

/** Legacy custom URI scheme kept for older rows and picker callbacks. */
const AVATAR_URI_PREFIX = "qb-avatar:";

function readKnownParam<T extends string>(
  value: string | null,
  allowedValues: readonly T[],
): T | undefined {
  return value && (allowedValues as readonly string[]).includes(value) ? value as T : undefined;
}

/** Whether a value is a known free avatar color key. */
export function isAvatarColor(value: string | null | undefined): value is AvatarColor {
  return typeof value === "string" && (AVATAR_COLORS as readonly string[]).includes(value);
}

/**
 * Get the local asset path for the avatar base. Always returns the default skin's bald body
 * (callers that need a different skin should use `getSkinPart(id).asset` directly).
 * Kept tolerant of legacy 1-arg call sites.
 */
export function getAvatarAsset(_legacy?: string | null | undefined): string {
  void _legacy;
  return getSkinPart(DEFAULT_SKIN_ID).asset;
}

/**
 * Resolve any avatar value (URL, color key, or qb-avatar URI) to a renderable URL.
 * - http/data/path URLs pass through
 * - everything else maps to the default skin asset
 */
export function resolveAvatarUrl(value: string | null | undefined): string {
  const raw = value?.trim();
  if (!raw) return getSkinPart(DEFAULT_SKIN_ID).asset;
  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:image/") ||
    raw.startsWith("/")
  ) {
    return raw;
  }
  // Try decoding qb-avatar URI → return the underlying skin asset
  const decoded = decodeAvatarCustomization(raw);
  if (decoded) return getSkinPart(decoded.skin).asset;
  return getSkinPart(DEFAULT_SKIN_ID).asset;
}

/** Whether a string looks like a renderable URL (for `<img>`) vs a key / seed. */
export function isAvatarUrl(avatar: string): boolean {
  return avatar.startsWith("http") || avatar.startsWith("/") || avatar.startsWith("data:image/");
}

/* ───────── Customization encoding (qb-avatar URI) ───────── */

/**
 * Encode a customization to a legacy `qb-avatar:` URI.
 * New persistence should decode this and write `avatar_customization` instead of storing the URI.
 *   { skin: 'skin_male_white', jersey: 'jersey_green', hair: 'hair_boy_basic' }
 *   → 'qb-avatar:skin_male_white?jersey=jersey_green&hair=hair_boy_basic'
 */
export function encodeAvatarCustomization(c: AvatarCustomization): string {
  const params = new URLSearchParams();
  if (c.jersey) params.set("jersey", c.jersey);
  if (c.hair) params.set("hair", c.hair);
  if (c.glasses) params.set("glasses", c.glasses);
  if (c.facialHair) params.set("facial", c.facialHair);
  for (const slot of EXTRA_SLOTS) if (c[slot]) params.set(slot, c[slot]!);
  if (wlFramePlace(c.frame)) params.set("frame", c.frame!);
  if (c.hairColor && c.hairColor !== "natural") params.set("hairColor", c.hairColor);
  const qs = params.toString();
  return `${AVATAR_URI_PREFIX}${c.skin}${qs ? `?${qs}` : ""}`;
}

/**
 * Decode a `qb-avatar:` URI back into a customization. Returns null for non-encoded values
 * (URLs, asset paths, raw color strings).
 */
export function decodeAvatarCustomization(value: string | null | undefined): AvatarCustomization | null {
  if (!value || !value.startsWith(AVATAR_URI_PREFIX)) return null;
  const tail = value.slice(AVATAR_URI_PREFIX.length);
  const [skin, query] = tail.split("?", 2);
  const params = new URLSearchParams(query ?? "");
  return {
    skin: readKnownParam(skin || DEFAULT_SKIN_ID, SKIN_IDS) ?? DEFAULT_SKIN_ID,
    jersey: readKnownParam(params.get("jersey"), JERSEY_IDS),
    hair: readKnownParam(params.get("hair"), HAIR_IDS),
    glasses: readKnownParam(params.get("glasses"), GLASSES_IDS),
    facialHair: readKnownParam(params.get("facial"), FACIAL_HAIR_IDS),
    ...Object.fromEntries(EXTRA_SLOTS.flatMap(slot => {
      const id = params.get(slot);
      return id && getAvatarPart(id)?.slot === slot ? [[slot, id]] : [];
    })),
    ...(readKnownParam(params.get("hairColor"), HAIR_COLORS) ? { hairColor: readKnownParam(params.get("hairColor"), HAIR_COLORS) } : {}),
    ...(wlFramePlace(params.get("frame")) ? { frame: params.get("frame")! } : {}),
  };
}

/**
 * Resolve any avatar value to a normalized customization.
 *
 * Behavior:
 *  - If value is a `qb-avatar:` URI → decode and respect each slot literally (a missing slot stays
 *    missing, so users who explicitly removed an item keep it removed).
 *  - If value is an external URL (Google) → return fresh-user defaults. Google photos are not
 *    used as in-game avatars.
 *  - Otherwise (no saved value at all) → fresh-user defaults: light skin + green jersey + boy hair.
 */
/**
 * Merge anything encoded in `customization.base` into the customization.
 *
 * If the object carries any structured slot key (even set to undefined), those
 * slots are respected literally — a user who explicitly removed an item keeps
 * it removed. Otherwise slots fall back to what `base` encodes.
 */
export function customizationFromAvatarValue(value: string | null | undefined): AvatarCustomization {
  const decoded = decodeAvatarCustomization(value);
  if (decoded) {
    return {
      ...decoded,
      skin: decoded.skin || DEFAULT_SKIN_ID,
      jersey: decoded.jersey,
      hair: decoded.hair,
      glasses: decoded.glasses,
      facialHair: decoded.facialHair,
    };
  }
  if (typeof value === "string" && isAvatarUrl(value)) {
    return {
      skin: DEFAULT_SKIN_ID,
      jersey: DEFAULT_JERSEY_ID,
      hair: DEFAULT_HAIR_ID,
    };
  }
  return {
    skin: DEFAULT_SKIN_ID,
    jersey: DEFAULT_JERSEY_ID,
    hair: DEFAULT_HAIR_ID,
  };
}

export const HAIR_COLORS = ['natural', 'platinum', 'ginger', 'silver', 'blue_tips', 'pink_streaks'] as const;

/** Preserve explicit empty slots and all local wardrobe fields in every renderer. */
export function resolveAvatarCustomization(c: AvatarCustomization): AvatarCustomization {
  const defaults = customizationFromAvatarValue(c.base);
  const structured = ['skin', 'jersey', 'hair', 'glasses', 'facialHair', 'frame', ...EXTRA_SLOTS].some(slot => Object.prototype.hasOwnProperty.call(c, slot));
  return structured ? { ...c, skin: c.skin ?? defaults.skin } : { ...defaults, ...c };
}
